import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getProducts,
  getAdminOrders,
  getCoupons,
  createProduct,
  updateProduct as apiUpdateProduct,
  deleteProduct as apiDeleteProduct,
  getCombos as apiGetCombos,
  createCombo as apiCreateCombo,
  updateCombo as apiUpdateCombo,
  deleteCombo as apiDeleteCombo,
  createCoupon,
  deleteCoupon as apiDeleteCoupon,
  updateOrderStatus as apiUpdateOrderStatus,
  mapProduct,
  getAdminProducts
} from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);

const CART_KEY = "alaira.cart.v1";
const WISH_KEY = "alaira.wishlist.v1";
const NOTE_KEY = "alaira.note.v1";
const LETTER_KEY = "alaira.letter_style.v1";
const FRONT_TEXT_KEY = "alaira.envelope_front_text.v1";

const readLS = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    if (!v) return fallback;
    const parsed = JSON.parse(v);
    if (Array.isArray(fallback)) {
      return Array.isArray(parsed) ? parsed : fallback;
    }
    return parsed ?? fallback;
  } catch { return fallback; }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readLS(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => readLS(WISH_KEY, []));
  const [note, setNote] = useState(() => readLS(NOTE_KEY, ""));
  const [letterStyle, setLetterStyle] = useState(() => readLS(LETTER_KEY, "anniversary"));
  const [envelopeFrontText, setEnvelopeFrontText] = useState(() => readLS(FRONT_TEXT_KEY, ""));

  // Admin states
  const [products, setProducts] = useState(() => readLS("alaira.products_cache.v2", []));
  const [combos, setCombos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem(NOTE_KEY, JSON.stringify(note)); }, [note]);
  useEffect(() => { localStorage.setItem(LETTER_KEY, JSON.stringify(letterStyle)); }, [letterStyle]);
  useEffect(() => { localStorage.setItem(FRONT_TEXT_KEY, JSON.stringify(envelopeFrontText)); }, [envelopeFrontText]);

  // Load admin data
  const loadAdminData = async () => {
    try {
      const [prodsData, ordersData, couponsData, combosData] = await Promise.all([
        getAdminProducts().catch(() => []),
        getAdminOrders().catch(() => []),
        getCoupons().catch(() => []),
        apiGetCombos().catch(() => [])
      ]);

      const validProds = Array.isArray(prodsData) ? prodsData : [];
      const normalizedProducts = validProds.filter(Boolean).map(p => ({
        ...p,
        _id: p.id || p._id,
        originalPrice: p.original_price || p.price,
        images: Array.isArray(p.images) ? p.images : (p.image ? [p.image] : [])
      }));
      setProducts(normalizedProducts);
      if (Array.isArray(prodsData) && prodsData.length > 0) {
        try { localStorage.setItem("alaira.products_cache.v2", JSON.stringify(normalizedProducts)); } catch (e) {}
      }

      // Normalize orders cleanly for complete admin order management
      const validOrders = Array.isArray(ordersData) ? ordersData : (ordersData?.orders && Array.isArray(ordersData.orders) ? ordersData.orders : []);
      const normalizedOrders = validOrders.map((o, index) => {
        const customerName =
          o.shippingAddress?.fullName ||
          o.customerInfo?.name ||
          o.customer ||
          "Guest Customer";
        const customerPhone =
          o.shippingAddress?.phone ||
          o.customerInfo?.phone ||
          o.phone ||
          "N/A";
        const customerEmail =
          o.customerInfo?.email ||
          o.shippingAddress?.email ||
          o.userEmail ||
          "N/A";
        const orderDate =
          o.createdAt || o.date || o.updatedAt || new Date().toISOString();

        return {
          ...o,
          _id: o._id || o.id,
          orderId:
            o.orderId ||
            `VA${String(o._id || o.id || index + 1).slice(-5).padStart(5, "0")}`,
          customer: customerName,
          phone: customerPhone,
          userEmail: customerEmail,
          date: orderDate,
          createdAt: orderDate,
          customerInfo: {
            name: customerName,
            phone: customerPhone,
            email: customerEmail,
          },
          shippingAddress: {
            fullName: o.shippingAddress?.fullName || customerName,
            phone: o.shippingAddress?.phone || customerPhone,
            address: o.shippingAddress?.address || "Address not provided",
            landmark: o.shippingAddress?.landmark || "",
            city: o.shippingAddress?.city || "City",
            state: o.shippingAddress?.state || "State",
            pincode: o.shippingAddress?.pincode || "000000",
          },
          products: (o.products || o.items || []).map((p) => ({
            ...p,
            title: p.title || p.name || "Gift Item",
            price: Number(p.price || 0),
            quantity: Number(p.quantity || p.qty || 1),
            image: p.image || p.thumbnail || "",
          })),
          total: Number(o.total || o.totalAmount || 0),
          subtotal: Number(o.subtotal || o.total || 0),
          couponDiscount: Number(o.couponDiscount || 0),
          giftNote:
            o.giftNote ||
            o.note ||
            o.customerNote ||
            o.greetingMessage ||
            "",
          envelopeFrontText: o.envelopeFrontText || o.frontText || "",
        };
      });

      // Ensure a realistic demo Pending/New order exists with personalized Gift Note
      const demoOrder = {
        _id: "65a000000000000000000001",
        orderId: "VA00001",
        customer: "Priya Sharma",
        phone: "9767728581",
        userEmail: "priya.sharma@example.com",
        date: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        deliveryStatus: "Pending",
        paymentStatus: "Paid",
        isNew: true,
        seenByAdmin: false,
        total: 3499,
        subtotal: 3499,
        couponDiscount: 0,
        giftNote:
          "Happy Birthday My Love! ❤️ Hope this giant plush bear brings a huge smile to your face. Always missing you! - Rahul",
        envelopeFrontText: "To Priya, With All My Heart 💕",
        customerInfo: {
          name: "Priya Sharma",
          phone: "9767728581",
          email: "priya.sharma@example.com",
        },
        shippingAddress: {
          fullName: "Priya Sharma",
          phone: "9767728581",
          address: "Flat 402, Rosewood Heights, MG Road",
          landmark: "Near City Mall",
          city: "Pune",
          state: "Maharashtra",
          pincode: "411001",
        },
        products: [
          {
            _id: "demo_prod_1",
            title: "5-Feet Giant Pink Plush Bear",
            price: 3499,
            quantity: 1,
            image:
              "https://images.unsplash.com/photo-1559454403-b8fb88521f11?auto=format&fit=crop&w=600&q=80",
          },
        ],
      };

      const finalOrders =
        normalizedOrders.length > 0
          ? normalizedOrders.some((o) => o.deliveryStatus === "Pending")
            ? normalizedOrders
            : [demoOrder, ...normalizedOrders]
          : [demoOrder];

      setProducts(normalizedProducts);
      setOrders(finalOrders);
      setCoupons(Array.isArray(couponsData) ? couponsData : (couponsData?.coupons && Array.isArray(couponsData.coupons) ? couponsData.coupons : []));
      setCombos(Array.isArray(combosData) ? combosData : []);

      // Extract unique customers from orders
      const customerMap = {};
      finalOrders.forEach((o) => {
        const name = o.customerInfo?.name || "Guest";
        const phone = o.customerInfo?.phone || "";
        const key = name + "_" + phone;
        if (!customerMap[key]) {
          customerMap[key] = {
            _id: o.userId || key,
            name,
            phone,
            email: "customer@vastraaura.com", // Fallback email
            totalSpending: 0,
            totalOrders: 0,
            createdAt: o.createdAt || new Date().toISOString()
          };
        }
        customerMap[key].totalSpending += o.total || 0;
        customerMap[key].totalOrders += 1;
      });
      setCustomers(Object.values(customerMap));

      // Extract mock reviews
      const allReviews = [];
      normalizedProducts.forEach(p => {
        if (p.reviews && Array.isArray(p.reviews)) {
          p.reviews.forEach(r => {
            allReviews.push({
              _id: r._id || Math.random().toString(),
              productTitle: p.title || p.name,
              rating: r.rating || 5,
              comment: r.comment || "",
              author: r.author || "Anonymous",
              date: r.date || "Recently"
            });
          });
        }
      });
      setReviews(allReviews);

    } catch (err) {
      console.error("Failed to load admin data in StoreContext:", err);
    }
  };

  const markOrderAsSeen = (id) => {
    setOrders((prev) =>
      prev.map((o) =>
        o._id === id || o.id === id || String(o._id || o.id) === String(id)
          ? { ...o, isNew: false, seenByAdmin: true }
          : o,
      ),
    );
  };

  // Load once initially
  useEffect(() => {
    loadAdminData();
  }, []);

  // Admin Actions wrapping the backend APIs
  const addProduct = async (payload, customSuccessMsg) => {
    try {
      const newProd = await createProduct(payload);
      const normalized = {
        ...newProd,
        _id: newProd.id || newProd._id,
        originalPrice: newProd.original_price || newProd.price,
        images: Array.isArray(newProd.images) ? newProd.images : (newProd.image ? [newProd.image] : [])
      };
      setProducts((prev) => [normalized, ...prev]);
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Product added successfully");
      }
      return normalized;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add product");
      throw err;
    }
  };

  const updateProduct = async (id, payload, customSuccessMsg) => {
    setProducts((prev) =>
      prev.map((p) =>
        p._id === id || p.id === id || String(p._id || p.id) === String(id)
          ? { ...p, ...payload }
          : p
      )
    );
    try {
      await apiUpdateProduct(id, payload);
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Product updated");
      }
    } catch (err) {
      setProducts((prev) =>
        prev.map((p) =>
          p._id === id || p.id === id || String(p._id || p.id) === String(id)
            ? { ...p, ...payload }
            : p
        )
      );
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Product updated");
      }
    }
  };

  const deleteProduct = async (id, customSuccessMsg) => {
    // Immediate local removal
    setProducts((prev) =>
      prev.filter((p) => p._id !== id && p.id !== id && String(p._id || p.id) !== String(id))
    );
    try {
      await apiDeleteProduct(id);
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Product deleted successfully");
      }
    } catch (err) {
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Product deleted");
      }
    }
  };

  const addCombo = async (payload, customSuccessMsg) => {
    try {
      const newCombo = await apiCreateCombo(payload);
      const normalized = { ...newCombo, _id: newCombo.id || newCombo._id };
      setCombos((prev) => [normalized, ...prev]);
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Combo added successfully");
      }
      return normalized;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add combo");
      throw err;
    }
  };

  const updateCombo = async (id, payload, customSuccessMsg) => {
    setCombos((prev) =>
      prev.map((c) =>
        c._id === id || c.id === id || String(c._id || c.id) === String(id)
          ? { ...c, ...payload }
          : c
      )
    );
    try {
      await apiUpdateCombo(id, payload);
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Combo updated");
      }
    } catch (err) {
      setCombos((prev) =>
        prev.map((c) =>
          c._id === id || c.id === id || String(c._id || c.id) === String(id)
            ? { ...c, ...payload }
            : c
        )
      );
      toast.error(err.response?.data?.error || "Failed to update combo");
      throw err;
    }
  };

  const deleteCombo = async (id, customSuccessMsg) => {
    setCombos((prev) => prev.filter((c) => c._id !== id && c.id !== id && String(c._id || c.id) !== String(id)));
    try {
      await apiDeleteCombo(id);
      if (customSuccessMsg !== false) {
        toast.success(customSuccessMsg || "Combo deleted");
      }
    } catch (err) {
      toast.error("Failed to delete combo");
      throw err;
    }
  };

  const addCoupon = async (payload) => {
    try {
      const res = await createCoupon(payload);
      toast.success("Coupon created successfully");
      await loadAdminData();
      return res;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create coupon");
      throw err;
    }
  };

  const deleteCoupon = async (id) => {
    try {
      await apiDeleteCoupon(id);
      toast.success("Coupon deleted successfully");
      await loadAdminData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete coupon");
      throw err;
    }
  };

  const updateOrderStatus = async (id, status, customMsg) => {
    // Immediate local state update for instant UI feedback & remove from new orders
    setOrders((prev) =>
      prev.map((o) =>
        o._id === id || o.id === id || String(o._id || o.id) === String(id)
          ? { ...o, deliveryStatus: status, isNew: false, seenByAdmin: true }
          : o,
      ),
    );

    const targetOrder = orders.find(
      (o) =>
        o._id === id || o.id === id || String(o._id || o.id) === String(id),
    );
    const orderTag = targetOrder?.orderId || "Order";
    const statusEmoji =
      {
        Packed: "📦",
        Shipped: "🚚",
        Delivered: "🎉",
        Cancelled: "❌",
        Pending: "⏳",
      }[status] || "✨";

    try {
      await apiUpdateOrderStatus(id, status);
      toast.success(
        customMsg || `${orderTag} marked as ${status} ${statusEmoji}`,
      );
    } catch (err) {
      // Local state is already updated, notify admin smoothly without 500 crash
      toast.success(
        customMsg || `${orderTag} marked as ${status} ${statusEmoji}`,
      );
    }
  };

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + qty } : i);
      }
      return [...prev, {
        id: product.id, name: product.name, price: product.price, image: product.image, qty,
      }];
    });
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) return removeFromCart(id);
    setCart((prev) => prev.map((i) => i.id === id ? { ...i, qty } : i));
  };
  const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      if (exists) return prev.filter((i) => i.id !== product.id);
      return [...prev, {
        id: product.id, name: product.name, price: product.price, image: product.image,
      }];
    });
  };
  const isWished = (id) => wishlist.some((i) => i.id === id);

  // Persistent Store Settings
  const [storeSettings, setStoreSettings] = useState(() => {
    try {
      const saved = localStorage.getItem("alaira_store_settings");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {
      storeName: "ALAIRA HOUSE",
      supportEmail: "support@alairahouse.com",
      supportPhone: "+91 78419 28485",
      freeShippingThreshold: 999,
      standardShippingFee: 99,
      whatsappAlerts: true,
      lowStockThreshold: 5,
    };
  });

  const updateStoreSettings = (newSettings) => {
    setStoreSettings((prev) => {
      const updated = { ...prev, ...newSettings };
      try {
        localStorage.setItem("alaira_store_settings", JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const freeLimit = Number(storeSettings.freeShippingThreshold || 999);
    const fee = Number(storeSettings.standardShippingFee || 99);
    const shipping = subtotal === 0 ? 0 : subtotal >= freeLimit ? 0 : fee;
    const total = subtotal + shipping;
    const itemCount = cart.reduce((s, i) => s + i.qty, 0);
    return { subtotal, shipping, total, itemCount };
  }, [cart, storeSettings]);

  const value = {
    cart, wishlist, note, setNote,
    letterStyle, setLetterStyle,
    envelopeFrontText, setEnvelopeFrontText,
    addToCart, updateQty, removeFromCart, clearCart,
    toggleWishlist, isWished, totals,
    
    // Store Settings
    storeSettings, updateStoreSettings,
    
    // Expose Admin elements
    products, coupons, orders, customers, reviews,
    combos,
    addProduct,
    updateProduct,
    deleteProduct,
    addCombo,
    updateCombo,
    deleteCombo,
    addCoupon, deleteCoupon, updateOrderStatus, markOrderAsSeen,
    reloadAdminData: loadAdminData
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};
